import { FormLeavingListener } from '@exam-management/components';
import { useQConfig, type QConfigInformation } from '@exam-management/feature-qconfig-provider';
import {
  GetQConfigByIdDocument,
  QConfigRandomType,
  QConfigStatus,
  QConfigTimeLimitType,
  useUpdateQconfigSettingsAsDraftMutation,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { QCONFIG_PUBLISH_ERROR_MESSAGES } from '@exam-management/qconfig-detail';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { type PropsWithChildren, dayjs } from '@shared/utils';
import uniqueId from 'lodash/uniqueId';
import { useEffect, useImperativeHandle, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { publishManualErrors } from './constants';
import updateQConfigSettingsAsDraftMapper from './mapper';
import {
  type QConfigSettingsFormInput,
  getQConfigSettingsFormSchema,
} from './qconfig-settings-form-schema';

type QConfigSettingsFormProviderProps = PropsWithChildren;

function getQConfigSettingsDefaultValues({
  structures,
  passingCriteria,
  timeLimit,
  randomType,
}: Pick<QConfigInformation, 'structures' | 'passingCriteria' | 'timeLimit' | 'randomType'>) {
  const structuresPassingCriteria = structures.reduce<Record<string, number | null>>(
    (acc, structure) => {
      const { id, passingCriteria } = structure;
      return {
        ...acc,
        [id]: passingCriteria?.value ?? null,
      };
    },
    {},
  );

  return {
    passingCriteria: {
      overall: passingCriteria?.value ?? null,
      structures: structuresPassingCriteria,
    },
    examTiming: {
      option: timeLimit?.type ?? null,
      overall:
        timeLimit?.type === QConfigTimeLimitType.All
          ? dayjs()
              .startOf('day')
              .add(
                timeLimit.values.reduce((acc, cur) => (acc += cur.value), 0),
                'second',
              )
              .toDate()
          : null,
      structures:
        timeLimit?.type === QConfigTimeLimitType.ByStructure
          ? timeLimit.values.map((value) => ({
              id: uniqueId('structure-'),
              parts:
                value.qConfigStructureIds?.map((structureId) => ({
                  label: structures.find((structure) => structureId === structure.id)?.name ?? '',
                  value: structureId,
                })) ?? [],
              timeLimit: dayjs().startOf('day').add(value.value, 'second').toDate(),
            }))
          : [],
    },
    randomization: randomType !== QConfigRandomType.RandomByPart ? randomType : null,
  };
}

export default function QConfigSettingsFormProvider(
  props: Readonly<QConfigSettingsFormProviderProps>,
) {
  const { children } = props;

  const { formMethodsRef, qConfigData, publishQConfig } = useQConfig();
  const {
    id: qConfigId,
    fullScore,
    structures,
    passingCriteria,
    timeLimit,
    randomType,
    status,
  } = qConfigData;

  const hasPermissionUpdate = useHasPermission(APP_PERMISSIONS.EXAM.QCONFIG.UPDATE);
  const isEnableEnsureLeaving = hasPermissionUpdate && status === QConfigStatus.Draft;

  const { showSnackbar } = useSnackbar();
  const [updateQConfigSettingsAsDraft] = useUpdateQconfigSettingsAsDraftMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQConfigByIdDocument,
        variables: {
          getQConfigByIdInput: {
            qConfigId: qConfigId,
          },
        },
      },
    ],
  });

  const QConfigSettingsFormSchema = useMemo(
    () => getQConfigSettingsFormSchema(fullScore, structures),
    [fullScore, structures],
  );

  const defaultValues = useMemo(
    () =>
      getQConfigSettingsDefaultValues({
        structures,
        passingCriteria,
        timeLimit,
        randomType,
      }),
    [structures, passingCriteria, timeLimit, randomType],
  );

  const formContext = useForm<QConfigSettingsFormInput>({
    defaultValues,
    resolver: zodResolver(QConfigSettingsFormSchema),
    mode: publishQConfig.errors?.length ? 'onChange' : 'onSubmit',
  });

  useEffect(() => {
    formContext.reset(defaultValues);
  }, [defaultValues, formContext]);

  const handleSaveQConfigSettings = async (data: QConfigSettingsFormInput) => {
    const updateQConfigSettingsAsDraftInput = updateQConfigSettingsAsDraftMapper(data, qConfigId);
    await updateQConfigSettingsAsDraft({
      variables: {
        updateQConfigSettingsAsDraftInput: updateQConfigSettingsAsDraftInput,
      },
      onCompleted: () => {
        showSnackbar({ message: 'QConfig has been saved', variant: 'success' });
        formContext.reset(data, {
          keepDefaultValues: true,
        });
        publishQConfig.clearErrors();
      },
      onError: () => {
        showSnackbar({ message: 'Failed to save, please try again', variant: 'error' });
      },
    });
  };

  useImperativeHandle(
    formMethodsRef,
    () => ({ formContext, onSubmit: handleSaveQConfigSettings }),
    [formContext],
  );

  const publishErrorCodes = (publishQConfig.errors ?? []).map((e) => e.code);

  useEffect(() => {
    const publishErrors = publishQConfig.errors ?? [];
    if (publishErrors.length) {
      for (const error of publishManualErrors) {
        if (publishErrorCodes.includes(error.key)) {
          formContext.setError(error.fieldName as Parameters<typeof formContext.setError>[0], {
            type: 'required',
            message: QCONFIG_PUBLISH_ERROR_MESSAGES[error.key],
            types: error.types,
          });
        }
      }
    }
  }, [publishQConfig.errors, formContext]);

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSaveQConfigSettings)}>{children}</Form>
      {isEnableEnsureLeaving ? <FormLeavingListener onSubmit={handleSaveQConfigSettings} /> : null}
    </FormProvider>
  );
}
